import React, { useEffect, useState } from 'react';
import {
  Box, Heading, Table, Thead, Tr, Th, Td, Tbody,
  Button, Spinner, Text,
  Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalCloseButton, ModalFooter,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { apiFetch } from './api';   // <-- import apiFetch

const API_BASE = 'https://n441mm4tfl.execute-api.eu-west-2.amazonaws.com/prod';

type ApiStageObj = { apiId: string; stage: string };
type UsagePlan = {
  id: string;
  name: string;
  description?: string;
  quota?: { limit: number; period: string };
  throttle?: { rateLimit: number; burstLimit: number };
  apiStages?: (string | ApiStageObj)[];
};

type ApiKey = {
  id: string;
  name: string;
  enabled: boolean;
  usagePlans: { id: string; name: string }[];
};

type ApiDef = {
  id: string;
  name: string;
  stages: string[];
};

export default function UsagePlanManager() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [usagePlans, setUsagePlans] = useState<UsagePlan[]>([]);
  const [apis, setApis] = useState<ApiDef[]>([]);
  const [expiryMap, setExpiryMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<UsagePlan | null>(null);

  const openPlanModal = (plan: UsagePlan) => {
    setSelectedPlan(plan);
    setIsPlanModalOpen(true);
  };

  // Fetch all required data
  const fetchAll = async () => {
    setLoading(true);
    try {
      // API keys
      const keyRes = await apiFetch(`${API_BASE}/listapikeys-gateway`);
      setApiKeys(await keyRes.json());

      // Usage plans
      const planRes = await apiFetch(`${API_BASE}/listusageplans`);
      setUsagePlans(await planRes.json());

      // APIs + stages
      const apiRes = await apiFetch(`${API_BASE}/listapisandstages`);
      const apiData = await apiRes.json();
      const grouped: Record<string, ApiDef> = {};
      apiData.forEach((item: any) => {
        const apiId = item.domain + ':' + item.path;
        if (!grouped[apiId]) {
          grouped[apiId] = {
            id: apiId,
            name: item.path,
            stages: [],
          };
        }
        if (item.stage && !grouped[apiId].stages.includes(item.stage)) {
          grouped[apiId].stages.push(item.stage);
        }
      });
      setApis(Object.values(grouped));

      // Expiry dates from DynamoDB Lambda
      const expiryRes = await apiFetch(`${API_BASE}/getapikeyexpiries`);
      const expiryData = await expiryRes.json();
      setExpiryMap(expiryData);

    } catch (err) {
      console.error(err);
      setError('Failed to load data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []);

  function getPlanDetails(planId: string) {
    return usagePlans.find(p => p.id === planId);
  }

  function getApiKeysForPlan(planId: string) {
    return apiKeys.filter(k => k.usagePlans.some(p => p.id === planId));
  }

  function getApiStages(plan: UsagePlan) {
    if (!plan.apiStages) return [];
    return plan.apiStages.map((as, idx) => {
      let apiId: string | undefined = undefined;
      let stage: string | undefined = undefined;
      if (typeof as === 'string') {
        [apiId, stage] = as.split(':');
      } else {
        apiId = as.apiId;
        stage = as.stage;
      }
      return {
        apiId,
        stage,
        key: `${apiId}-${stage}-${idx}`,
      };
    });
  }

  if (loading) return (
    <Box px={10} py={10} minH="100vh" bg="brand.900" color="brand.yellow">
      <Spinner size="xl" color="brand.yellow" />
    </Box>
  );

  return (
    <Box px={10} py={10} minH="100vh" bg="brand.900" color="brand.yellow">
      {/* Section 1: API Keys Table */}
      <Heading mb={8}>API Gateway Key Management</Heading>
      <Table colorScheme="yellow" bg="yellow.100" borderRadius="md" color="black" mb={16}>
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Enabled</Th>
            <Th>Expiry</Th>
            <Th>Attached Usage Plan(s)</Th>
            <Th>Attach Usage Plan</Th>
          </Tr>
        </Thead>
        <Tbody>
          {apiKeys.map(key => (
            <Tr key={key.id}>
              <Td>{key.name}</Td>
              <Td>{key.enabled ? 'Yes' : 'No'}</Td>
              <Td>{expiryMap[key.name] || '-'}</Td>
              <Td>
                {key.usagePlans && key.usagePlans.length > 0
                  ? key.usagePlans.map((p, idx) => {
                      const plan = getPlanDetails(p.id);
                      return (
                        <Box key={`${p.id}-${idx}`} mb={1}>
                          <Text
                            as="span"
                            fontWeight="bold"
                            color="yellow.600"
                            cursor="pointer"
                            _hover={{ textDecoration: 'underline' }}
                            onClick={() => plan && openPlanModal(plan)}
                          >
                            {plan?.name || p.name}
                          </Text>
                        </Box>
                      );
                    })
                  : <Text color="gray.500">None</Text>
                }
              </Td>
              <Td>
                <Button
                  as={RouterLink}
                  to={`/attach-plan/${key.id}`}
                  state={{ keyName: key.name }}
                  size="sm"
                  colorScheme="yellow"
                >
                  Attach Plan
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* Section 2: Usage Plans Overview Table */}
      <Heading mb={8}>Usage Plans Overview</Heading>
      <Table colorScheme="yellow" bg="yellow.100" borderRadius="md" color="black">
        <Thead>
          <Tr>
            <Th>Plan Name</Th>
            <Th>Description</Th>
            <Th>Quota</Th>
            <Th>Throttle</Th>
            <Th>Associated API Keys</Th>
            <Th>APIs</Th>
          </Tr>
        </Thead>
        <Tbody>
          {usagePlans.map(plan => (
            <Tr key={plan.id}>
              <Td>
                <Text fontWeight={700}>{plan.name}</Text>
              </Td>
              <Td>{plan.description || '-'}</Td>
              <Td>
                {plan.quota
                  ? `${plan.quota.limit} per ${plan.quota.period}`
                  : '-'}
              </Td>
              <Td>
                {plan.throttle
                  ? `${plan.throttle.rateLimit} req/s, burst ${plan.throttle.burstLimit}`
                  : '-'}
              </Td>
              <Td>
                {getApiKeysForPlan(plan.id).length > 0
                  ? getApiKeysForPlan(plan.id).map((k, idx) =>
                      <Text key={`${k.id}-${idx}`} as="span" display="block">{k.name}</Text>
                    )
                  : <Text color="gray.500">None</Text>}
              </Td>
              <Td>
                {getApiStages(plan).length > 0
                  ? getApiStages(plan).map(s =>
                      <Text key={s.key} as="span" display="block">
                        {s.stage}
                      </Text>
                    )
                  : <Text color="gray.500">None</Text>}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* Modal for plan details */}
      <Modal isOpen={isPlanModalOpen} onClose={() => setIsPlanModalOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent bg="brand.800" color="brand.yellow">
          <ModalHeader>{selectedPlan?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedPlan && (
              <Box>
                {selectedPlan.description && <Text mb={2}>Description: {selectedPlan.description}</Text>}
                {selectedPlan.quota && (
                  <Text mb={2}>
                    Quota: {selectedPlan.quota.limit} per {selectedPlan.quota.period}
                  </Text>
                )}
                {selectedPlan.throttle && (
                  <Text mb={2}>
                    Throttle: {selectedPlan.throttle.rateLimit} req/s, Burst {selectedPlan.throttle.burstLimit}
                  </Text>
                )}
                {selectedPlan.apiStages && selectedPlan.apiStages.length > 0 && (
                  <Box mt={4}>
                    <Text fontWeight="bold" mb={1}>Associated APIs:</Text>
                    {selectedPlan.apiStages.map((s: any, idx: number) => {
                      const stage = typeof s === 'string' ? s.split(':')[1] : s.stage;
                      return <Text key={idx} fontSize="sm">{stage}</Text>;
                    })}
                  </Box>
                )}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" color="brand.yellow" onClick={() => setIsPlanModalOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
